import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { normalizeInternationalPhone } from '../utils/phone';
import './BookContactEditor.css';

const API = process.env.REACT_APP_BACKEND_URL;
const METHODS = ['chat', 'phone', 'whatsapp', 'email'];
const LEGACY_MAP = {
  chat: ['chat'], phone: ['phone'], whatsapp: ['whatsapp'],
  chat_phone: ['chat', 'phone'], chat_whatsapp: ['chat', 'whatsapp'], both: ['chat', 'phone'],
};

export const BookContactEditor = ({ bookId, open, onClose, onSaved }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [methods, setMethods] = useState(['chat']);
  const [phone, setPhone] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});

  const labels = {
    chat: t('cb.contactChat'),
    phone: t('cb.contactPhone'),
    whatsapp: t('cb.contactWhatsApp'),
    email: t('cb.contactEmail'),
  };

  useEffect(() => {
    if (!open || !token) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErrors({});
      try {
        const res = await fetch(`${API}/api/books/user/me`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        const mine = (data.books || []).find(b => b.book_id === bookId);
        if (mine && !cancelled) {
          setMethods(mine.contact_methods?.length ? mine.contact_methods : (LEGACY_MAP[mine.contact_method] || ['chat']));
          setPhone(mine.phone || '');
          setWhatsapp(mine.whatsapp || '');
          setEmail(mine.contact_email || '');
        }
      } catch (e) { /* keep defaults */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [open, token, bookId]);

  const toggle = (m) => {
    setMethods(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]);
    setErrors({});
  };

  const save = async () => {
    const errs = {};
    if (methods.length === 0) errs.methods = t('cb.errContactMethod');
    if (methods.includes('phone')) {
      if (!phone.trim()) errs.phone = t('cb.errPhone');
      else if (!normalizeInternationalPhone(phone.trim()).valid) errs.phone = t('cb.errPhoneInvalid');
    }
    if (methods.includes('whatsapp')) {
      const wa = whatsapp.trim() || phone.trim();
      if (!wa) errs.whatsapp = t('cb.errWhatsapp');
      else if (!normalizeInternationalPhone(wa).valid) errs.whatsapp = t('cb.errWhatsappInvalid');
    }
    if (methods.includes('email')) {
      const em = email.trim();
      if (!em || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) errs.email = t('cb.errEmail');
    }
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/books/${bookId}/contact`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          contact_methods: methods,
          phone: phone.trim() || null,
          whatsapp: methods.includes('whatsapp') ? (whatsapp.trim() || null) : null,
          contact_email: methods.includes('email') ? email.trim() : null,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(t('bookdeta.contactsUpdated'));
        onSaved?.(data.contact);
      } else {
        toast.error(typeof data.detail === 'string' ? data.detail : t('bookdeta.updateFailed'));
      }
    } catch (e) {
      toast.error(t('bookdeta.updateFailed'));
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent dir={isRTL ? 'rtl' : 'ltr'} className="bce-content" data-testid="book-contact-editor">
        <DialogHeader>
          <DialogTitle>{t('bookdeta.editContacts')}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="bce-loading" data-testid="bce-loading"><Loader2 size={22} className="bce-spin" /></div>
        ) : (
          <div className="bce-body">
            <div className="bce-chips" data-testid="bce-chips">
              {METHODS.map(m => (
                <button
                  key={m}
                  type="button"
                  className={`bce-chip ${methods.includes(m) ? 'active' : ''}`}
                  onClick={() => toggle(m)}
                  data-testid={`bce-chip-${m}`}
                >
                  {methods.includes(m) && <Check size={14} />}
                  {labels[m]}
                </button>
              ))}
            </div>
            {errors.methods && <p className="bce-error" data-testid="bce-error-methods">{errors.methods}</p>}
            <p className="bce-hint">{t('cb.contactHint')}</p>

            {methods.includes('phone') && (
              <div className="bce-field">
                <label>{t('phone')}</label>
                <input type="tel" dir="ltr" placeholder="+249 xxx xxx xxx" value={phone} onChange={e => setPhone(e.target.value)} data-testid="bce-field-phone" />
                {errors.phone && <p className="bce-error" data-testid="bce-error-phone">{errors.phone}</p>}
              </div>
            )}
            {methods.includes('whatsapp') && (
              <div className="bce-field">
                <label>{t('cb.whatsappLabel')}</label>
                <input type="tel" dir="ltr" placeholder="+249 xxx xxx xxx" value={whatsapp} onChange={e => setWhatsapp(e.target.value)} data-testid="bce-field-whatsapp" />
                {phone.trim() && !whatsapp.trim() && <p className="bce-hint">{t('cb.whatsappFallbackHint')}</p>}
                {errors.whatsapp && <p className="bce-error" data-testid="bce-error-whatsapp">{errors.whatsapp}</p>}
              </div>
            )}
            {methods.includes('email') && (
              <div className="bce-field">
                <label>{t('cb.emailLabel')}</label>
                <input type="email" dir="ltr" placeholder="name@example.com" value={email} onChange={e => setEmail(e.target.value)} data-testid="bce-field-email" />
                {errors.email && <p className="bce-error" data-testid="bce-error-email">{errors.email}</p>}
              </div>
            )}

            <div className="bce-actions">
              <Button variant="outline" onClick={onClose} data-testid="bce-cancel">{t('cancel')}</Button>
              <Button onClick={save} disabled={saving} data-testid="bce-save">
                {saving ? <Loader2 size={16} className="bce-spin" /> : t('save')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookContactEditor;
